//Author June Leow
//Date June 10th, 2024
import {getReducer, getSetStateFunction, getAPICallGenerator, postAPICallGenerator, putAPICallGenerator, deleteAPICallGenerator, callBackGenerator, showMessage, formatNumber, formatDateTime, getSession} from '../../util/util';
import AppraisalFile from '../file/file';
import {Alert, Collapse, Input, Nav, NavItem, Progress, Row, Col, Button, Card, CardHeader, CardBody, Modal, ModalBody, ModalHeader} from 'reactstrap';
import './appraisal.css';
import Comment from '../comment/comment';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import {NavLink, useParams, useNavigate} from 'react-router-dom';
import React, {useReducer, useEffect} from 'react';
import 'react-datepicker/dist/react-datepicker.css';

const uad36DropdownFieldKeys = ['uad_36_order_type','uad_36_completion_report_type','uad_36_valuation_method','uad_36_inspection_type','uad_36_property_type','uad_36_unit_type','uad_36_attachment_type','uad_36_pdu_indicator','uad_36_property_legal_structure_type','uad_36_land_owned_common_indicator','uad_36_construction_method_type','uad_36_government_agency','uad_36_loan_classification','uad_36_property_usage','uad_36_value_condition','uad_36_valuation_timing','uad_36_additional_report','uad_36_renovation_program','uad_36_pdr_agency','uad_36_loan_program'];

//initialize the state
const initialState = {
  submitDisabled:false, 
  appraisal: {},
  overallStatus:{
    review_count:0,
    condition_count:0,
    rebuttal_count:0,
    due_invoice_count:0
  },
  loanTypes:[],
  loanPurposes:[],
  propertyTypes:[],
  statuses:[],
  dropdownOpen: false,
  waitingOnContactDropdownOpen: false,
  old_special_instructions:'',
  newClientEntityLabel:'',
  newClientEntityName:'',
  newClientEntityEmail:'',
  newClientEntityPhone:'',
  newClientEntityDropDownOpen:false,
  configureAppraisalTypeDropDownOpen:false,
  inspectionDate:'',
  declineReason:'',
  conditionReasons:[],
  conditionreason:'',
  conditionFee:'',
  conditionComment:'',
  conditionTurntime:'',
  conditionPopUp:false,
  declineReasons:[],
  declinePopUp:false,
  declineComment:'',
  inspectionComment:'',
  extraBorrowers:[],

  uad36DropdownOptions:{},
};

//reducer function that perform state update
const reducer = getReducer();


const Appraisal  = (props)=>{
  const controller = new AbortController();

  let id = useParams().id;
  let newInitialState = Object.assign({}, initialState, {
    id:id
  });

  const [state, dispatch] = useReducer(reducer,newInitialState);

  //wrapper function
  const setState = getSetStateFunction(dispatch);
  const history = useNavigate();
  const apiCallBack = callBackGenerator(setState);
  const httpGet = getAPICallGenerator(props, {signal:controller.signal});
  const httpPost = postAPICallGenerator(props, {signal:controller.signal});
  const httpPut = putAPICallGenerator(props, {signal:controller.signal});
  const httpDelete = deleteAPICallGenerator(props, {signal:controller.signal});

  //run only once when component is loaded
  useEffect(()=>{
    getAppraisal(id);
    getOverallStatus(id);
    getDeclineReasons();
    getConditionReasons();
    return ()=> controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  useEffect(()=>{
    if(state.submitDisabled){
      scheduleInspection();
    }
  },[state.submitDisabled])

  const buttonHandler = () => {
    if(state.submitDisabled)
      return;
    setState({submitDisabled:true});
  }

  //non API call but simpyl manage state
  const updateStatus=(status)=>{
    let appraisal = Object.assign({},state.appraisal);
    appraisal.status = status;
    setState({appraisal:appraisal});
  }

  //update the instructions
  const changeInstructions=(e)=>{
    let appraisal = Object.assign({},state.appraisal,{entities:state.appraisal.entities},{special_instructions: e.target.value});
    setState({appraisal:appraisal});
  }

  const conditionPopUpToggle=()=>{
    setState({conditionPopUp:!state.conditionPopUp});
  }

  const declinePopUpToggle=()=>{
    setState({declinePopUp:!state.declinePopUp});
  }

  //toggle function for the instruction floating note
  const floatingMemoToggle=()=>{
    setState({
      dropdownOpen: !state.dropdownOpen
    });
  }

  //API call
  //get appraisal
  const getAppraisal=(id)=>{
    //get appraisal call back
    let callBack = (response)=>{
      let code = response.data.code;
      if(code==='00'){
        if(response.data.data)
          response.data.data.special_instructions = response.data.data.special_instructions.replace(/(?:\\[rn]|[\r\n]+)+/g, "\r\n");

        //hide speicla instruction from appraiser once inspection had been scheduled
        let hideSpecialInstructionStatus = ['Inspection Scheduled','Inspection Completed','Review','Shipping','Conditioned','Cancelled','Completed'];

        if(hideSpecialInstructionStatus.indexOf(response.data.data.status)!==-1)
          response.data.data.special_instructions = '';

        if(response.data.data.special_instructions!=='')
          setState({dropdownOpen:true})
        let extraBorrowers = [];
        for(let i=0;i<response.data.data.entities.length;i++)
          if(response.data.data.entities[i].entity_label==='Co-Borrower'||response.data.data.entities[i].entity_label==='Borrower')
            extraBorrowers.push(response.data.data.entities[i]);
        setState({appraisal:response.data.data,old_special_instructions:response.data.data.special_instructions,extraBorrowers:extraBorrowers});

        if(response.data.data.uad_version==='3.6')
          getUad36DropdownOptions();
      }
    };
    
    httpGet('appraisal/'+id, '', 'Oops, something went wrong and could not load this appraisal. Please try again later.', callBack)
  }

  const getUad36DropdownOptions = ()=>{
    for(let i=0;i<uad36DropdownFieldKeys.length;i++){
      let fieldKey = uad36DropdownFieldKeys[i];

      let callBack = (response)=>{
        if(response.data && response.data.code==='00')
          setState({uad36DropdownOptions:{[fieldKey]:response.data.data}},'CONCAT_SET_STATES');
      }

      httpGet('appraisal/uad-36-dropdown-option/'+fieldKey,'','Failed to retrieve UAD 3.6 dropdown options.',callBack);
    }
  }

  const getUad36Options = (field)=>{
    return state.uad36DropdownOptions[field] || [];
  }

  const getUad36DisplayValue = (field)=>{
    let value = state.appraisal[field];

    if(Array.isArray(value))
      return value.length>0 ? value.map((option)=>option.option_display_label || option.value || option.option_value).join(', ') : 'None Selected';

    if(value===null || value===undefined || value==='')
      return 'None Selected';

    let option = getUad36Options(field).find((item)=>{
      let optionValue = item.option_value !== undefined ? item.option_value : item.key;
      return optionValue===value;
    });

    if(option)
      return option.option_display_label !== undefined ? option.option_display_label : option.value;

    return value;
  }

  const renderUad36DisplayField = (label, field, enabled=true, required=false)=>{
    return <Row>
      <Col sm="4">
        {required ? <span className="red-color">*</span> : null}{label}
      </Col>
      <Col sm="8">
        {enabled ? getUad36DisplayValue(field) : 'None Selected'}
      </Col>
    </Row>;
  }

  //get overall status
  const getOverallStatus=()=>{
    const callBack = apiCallBack([{state:'overallStatus', key:'data'}]);
    httpGet('appraisal/overall/status/'+state.id, '', 'Oops, something went wrong and could not load the information of this appraisal. Please try again later.', callBack)
  }

  const getConditionReasons=()=>{
    const callBack = apiCallBack([{state:'conditionReasons', key:'data'}]);
    httpGet('appraisal/conditionReason/get', '', 'Oops, something went wrong and could not load the information of this appraisal. Please try again later.', callBack)
  }

  const getDeclineReasons=()=>{
    const callBack = apiCallBack([{state:'declineReasons', key:'data'}]);
    httpGet('appraisal/declineReason/get', '', 'Oops, something went wrong and could not load the information of this appraisal. Please try again later.', callBack)
  }


  const scheduleInspection=()=>{
    if(state.appraisal.status==='Inspection Scheduled'&&state.inspectionComment===''){
      showMessage('warning','Please provide the reason to update inspection date');
      setState({submitDisabled:false});
    }
    else if(state.inspectionDate===''){
      showMessage('warning','Please select the inspection date');
      setState({submitDisabled:false});
    }
    else{
      let parameters = [
        {
          field:'orderID',
          value:state.id
        },
        {
          field:'inspectionDate',
          value:state.inspectionDate
        },
        {
          field:'comment',
          value:state.inspectionComment
        }
      ];

      let callBack = (response)=>{
        let code = response.data.code;
        if(code==='00'){
          //clear the text area
          let appraisal = Object.assign({},state.appraisal);
          appraisal.datetime_inspection = state.inspectionDate;
          if(appraisal.status==='Pending Inspection'||appraisal.status==='Waiting On POC'||appraisal.status==='Auto Assigning')
            appraisal.status = 'Inspection Scheduled';
          setState({inspectionDate:'',appraisal:appraisal, submitDisabled:false});
        }
      };

      let errorCallBack = (error)=>{
        setState({submitDisabled:false});
      }
      
      httpPost('appraisal/inspection/schedule', parameters, 'Inspection set successfully.', 'Oops, something went wrong and could not set the inspection. Please try again later.', callBack, errorCallBack);
    }
  }

  const setInspectionCompleted=()=>{
    let parameters = [
      {
        field:'orderID',
        value:state.id
      }
    ];

    let callBack = (response)=>{
      let code = response.data.code;

      if(code==='00'){
        //clear the text area
        let appraisal = Object.assign({},state.appraisal);

        if(appraisal.status==='Inspection Scheduled')
          appraisal.status = 'Inspection Completed';

        setState({appraisal:appraisal});
      }
    };
    
    httpPost('appraisal/inspection/completed', parameters, 'Inspection completed successfully.', 'Oops, something went wrong and could not complete the inspection. Please try again later.', callBack);
  }

  const setAcceptance=(action)=>{
    let successMsg = 'Assignment accepted successfully.';
    if(action==='no')
      successMsg = 'Assignment declined successfully.';
    let errorMsg = 'Oops, something went wrong and could not accept the assignment. Please try again later.';
    if(action==='no')
      errorMsg = 'Oops, something went wrong and could not decline the assignment. Please try again later.';

    let parameters = [
      {
        field:'orderID',
        value:state.id
      },
      {
        field:'action',
        value:action
      }
    ];

    if(action==='no'){
      let tmp = {
        field:'reason',
        value:state.declineReason
      };

      parameters.push(tmp);

      tmp = {
        field:'comment',
        value:state.declineComment
      }

      parameters.push(tmp);
    }

    let callBack = (response)=>{
      let code = response.data.code;

      if(code==='00'){
        if(action==='no')
          history('/appraisal');
        else if(action==='yes'){
          getAppraisal(state.id);
        }
      }
    };
    let url = 'appraisal/assignment/acceptance';
    if(state.appraisal.status==='Auto Assigning')
      url = 'autoAssign/acceptance';
    httpPost(url, parameters, successMsg, errorMsg, callBack);
  }

  const conditionallyAccept=(e)=>{
    e.preventDefault();
    let parameters = [
      {
        field:'orderID',
        value:state.id
      },
      {
        field:'reason',
        value:state.conditionReason
      },
      {
        field:'fee',
        value:state.conditionFee
      },
      {
        field:'turntime',
        value:state.conditionTurntime
      },
      {
        field:'comment',
        value:state.conditionComment
      },
      {
        field:'appraiserFk',
        value:localStorage.getItem('typeID')
      }
    ];

    let callBack = (response)=>{
      let code = response.data.code;
      if(code==='00'){
        history('/appraisal');
      }
    };
    httpPost('appraisal/assignment/conditionallyAccept', parameters, 'Assignment conditionally accepted successfully.', 'Oops, something went wrong and could not conditionally accept the assignment. Please try again later.', callBack);
  }

  //render
  //render the page with loading icon if the appraisal is not loaded
  let isUad36 = state.appraisal.uad_version==='3.6';

  let uad36ServiceType = state.appraisal.uad_36_order_type;
  let isUad36URAR = uad36ServiceType==='URARAppraisal' || uad36ServiceType==='Appraisal URAR';
  let isUad36PDR = uad36ServiceType==='PropertyDataReport' || uad36ServiceType==='Property Data Report';
  let isUad36CompletionReport = uad36ServiceType==='CompletionReport' || uad36ServiceType==='Completion Report';
  let isUad36Traditional = state.appraisal.uad_36_valuation_method==='Traditional' || state.appraisal.uad_36_valuation_method==='TraditionalAppraisal';

  let uad36PropertyType = state.appraisal.uad_36_property_type;
  let isUad36SingleFamily = uad36PropertyType==='SingleFamily' || uad36PropertyType==='Single Family' || uad36PropertyType==='SFR';
  let isUad36Condo = uad36PropertyType==='Condominium' || uad36PropertyType==='Condo';
  let isUad36Cooperative = uad36PropertyType==='Cooperative' || uad36PropertyType==='Co-Operative (Co-Op)';
  let isUad36URAROrPDR = isUad36URAR || isUad36PDR;

  let pdr, pdrTab;

  if(state.appraisal.upd_fk && state.appraisal.status!=='Assigned' && state.appraisal.status!=='Cancelled'){
    pdr = <div>
      <Alert color="warning" className="my-alert">
        This is a UPD order, click <NavLink target="_blank" to={"/uniform-property-dataset/"+state.appraisal.upd_fk}>here</NavLink> or <b>UPD tab</b> above to access the UPD form helper.
      </Alert>
    </div>;

    pdrTab = <NavItem>
      <NavLink className="nav-link" target="_blank" to={"/uniform-property-dataset/"+state.appraisal.upd_fk}><i className="fa fa-building-o sidebar-icon"></i> UPD</NavLink>
    </NavItem>;
  }

  let appraisalType;
  let appraisalFileUI;
  let appraisalCommentUI;

  let effectiveLoanPurpose = isUad36 ? state.appraisal.uad_36_valuation_assignment_type : state.appraisal.loan_purpose;

  if(state.appraisal.ID){
    appraisalCommentUI = <Comment status={state.appraisal.status} loan_purpose={effectiveLoanPurpose || ''} entities={state.appraisal.entities} appraisalFk={state.id} logout={props.logout} showLoading={props.dispatchShowLoadingFunc} hideLoading={props.dispatchHideLoadingFunc}/>;
  } else {
    appraisalCommentUI =
    <div>
      <div className="file-panel"></div>
      <div className="my-diviver"></div>
      <div className="align-right">
        <br/>
        <Button color="warning" className="cursor-pointer"><i className="fa fa-plus"></i> Post Comment</Button>
      </div>
    </div>;
  }

  let xmlRequired = false;
  let envRequired = false;
  if(state.appraisal&&state.appraisal.appraisal_type){

    if(state.appraisal.appraisal_type){

      appraisalType = <div className="display-inline">{state.appraisal.external_label}</div>;
    }


    let xmlRequiredType = ['1075','2055','1004c','1004','1073','1025','Condo Appraisal','SFR Appraisal','Family Multi','Manufactured Home Appraisal'];

    for(let i=0;i<xmlRequiredType.length;i++){
      if(state.appraisal.appraisal_type.includes(xmlRequiredType[i])){
        if(state.appraisal.appraisal_type.includes('1004D')){
          if(state.appraisal.appraisal_type.includes('Final Inspection with Photos (1004D / HUD 92051)')){
            xmlRequired = true;
          }
          else{
            continue;
          }
        }

        xmlRequired = true;
      }
    }

    if(!isUad36 && state.appraisal.loan_type==='Private')
      xmlRequired = false;


    if(xmlRequired && state.appraisal.env_required=='yes')
      envRequired = true;

    appraisalFileUI = <AppraisalFile updateStatus={updateStatus} appraisalType={state.appraisal.appraisal_type} uploadType="Orders" xmlRequired={xmlRequired} envRequired={envRequired} uad36={isUad36} entities={state.appraisal.entities} appraisalFk={state.id} logout={props.logout} showLoading={props.dispatchShowLoadingFunc} hideLoading={props.dispatchHideLoadingFunc}/>;
  }
  else {
    appraisalFileUI  =
      <div>
        <div className="file-panel"></div>
        <div className="my-diviver"></div>
        <div className="align-right">
          <br/>
          <Button color="warning" className="cursor-pointer"><i className="fa fa-plus"></i> Upload File</Button>
        </div>
      </div>;
  }


  let progressBar;
  let actionBar;
  let inspectionDate;

  if(state.inspectionDate!=='')
    inspectionDate = moment(state.inspectionDate).toDate();
  progressBar = <Progress animated value={100} className="custom-progress custom-progress-blue"><b>{state.appraisal.status}</b></Progress>;
  let required = false;
  let conditionFee, conditionTurntime, conditionTurntimeValue;

  if(state.conditionTurntime!=='')
    conditionTurntimeValue = moment(state.conditionTurntime).toDate();

  if(state.conditionReason==='Fee'||state.conditionReason==='Fee & Turntime')
    conditionFee =
    <div>
      <label>Fee</label>
      <Input type="text" value={state.conditionFee} onChange={(e)=>setState({conditionFee:e.target.value})}/>
    </div>;

  if(state.conditionReason==='Turntime'||state.conditionReason==='Fee & Turntime')
    conditionTurntime =
    <div>
      <label>Due date</label><br/>
      <DatePicker
        onChangeRaw={(e)=>{e.preventDefault()}}
        className="form-control"
        selected={conditionTurntimeValue}
        onChange={(text)=>{console.log(text);setState({conditionTurntime:text.toLocaleDateString('en-CA')})}}
      />
    </div>;

  let purchasePrice;
  let showPurchasePrice = isUad36 ? (isUad36URAR && state.appraisal.uad_36_valuation_assignment_type==='Purchase') : state.appraisal.loan_purpose==='Purchase';
  if(showPurchasePrice)
    purchasePrice =
    <Row>
      <Col sm="4">
        Purchase Price
      </Col>
      <Col sm="8">
        ${formatNumber(state.appraisal.selling_price)}
      </Col>
    </Row>;

  let fhaCaseNumber;
  if(!isUad36 && state.appraisal.loan_type==='FHA')
    fhaCaseNumber =
    <Row>
      <Col sm="4">
        FHA Case Number
      </Col>
      <Col sm="8">
        {state.appraisal.fha_case_number}
      </Col>
    </Row>;

  let uad36ServiceScope;
  let uad36LoanAssignment;
  let uad36PropertyInformation;

  if(isUad36){
    uad36ServiceScope = <div><Card>
      <CardHeader><b><i className="fa fa-tasks"></i>&nbsp;Service &amp; Scope</b></CardHeader>
      <CardBody>
        {renderUad36DisplayField('Service Type','uad_36_order_type',true,true)}
        {renderUad36DisplayField('Completion Report Type','uad_36_completion_report_type',isUad36CompletionReport,isUad36CompletionReport)}
        {renderUad36DisplayField('Valuation Method','uad_36_valuation_method',isUad36URAR,isUad36URAR)}
        {renderUad36DisplayField('Inspection Type','uad_36_inspection_type',isUad36URAR && isUad36Traditional,isUad36URAR && isUad36Traditional)}
        {renderUad36DisplayField('PDR Agency','uad_36_pdr_agency',isUad36PDR,isUad36PDR)}
      </CardBody>
    </Card>
    <br/>
    </div>;

    uad36LoanAssignment = <div>
      {renderUad36DisplayField('UAD Version','uad_version',true,false)}
      {renderUad36DisplayField('Loan Program','uad_36_loan_program',true,false)}
      {renderUad36DisplayField('Other Loan Program','uad_36_other_loan_program',state.appraisal.uad_36_loan_program==='Other',false)}
      {renderUad36DisplayField('Assignment Reason','uad_36_valuation_assignment_type',isUad36URAR,isUad36URAR)}
      {purchasePrice}
      {renderUad36DisplayField('Government Agency','uad_36_government_agency',isUad36URAR,isUad36URAR)}
      {renderUad36DisplayField('Loan Classification','uad_36_loan_classification',isUad36URAR,isUad36URAR)}
      {renderUad36DisplayField('Occupancy Intent','uad_36_property_usage',isUad36URAR,isUad36URAR)}
      {renderUad36DisplayField('Valuation Timing','uad_36_valuation_timing',isUad36URAR,false)}
      {renderUad36DisplayField('Retrospective Effective Date','uad_36_retrospective_effective_date',isUad36URAR && state.appraisal.uad_36_valuation_timing==='Retrospective',isUad36URAR && state.appraisal.uad_36_valuation_timing==='Retrospective')}
      {renderUad36DisplayField('Additional Reports','uad_36_additional_report',isUad36URAR,false)}
      {renderUad36DisplayField('Renovation Program','uad_36_renovation_program',isUad36URAR,false)}
      {renderUad36DisplayField('Other Renovation Program','uad_36_other_renovation_program',isUad36URAR && state.appraisal.uad_36_renovation_program==='Other',isUad36URAR && state.appraisal.uad_36_renovation_program==='Other')}
    </div>;

    uad36PropertyInformation = <div>
      {renderUad36DisplayField('Property Type','uad_36_property_type',true,true)}
      {renderUad36DisplayField('Unit Type','uad_36_unit_type',true,false)}
      {renderUad36DisplayField('Property Attachment','uad_36_attachment_type',true,isUad36SingleFamily)}
      {renderUad36DisplayField('Units Excluding ADUs','uad_36_unit_count',isUad36URAROrPDR,isUad36URAROrPDR)}
      {renderUad36DisplayField('ADU Count','uad_36_adu_count',isUad36URAROrPDR,isUad36URAROrPDR)}
      {renderUad36DisplayField('PUD','uad_36_pdu_indicator',isUad36SingleFamily || isUad36Condo,isUad36SingleFamily || isUad36Condo)}
      {renderUad36DisplayField('Project Legal Structure','uad_36_property_legal_structure_type',isUad36Condo || isUad36Cooperative,isUad36Condo || isUad36Cooperative)}
      {renderUad36DisplayField('Land Owned in Common','uad_36_land_owned_common_indicator',isUad36Condo || isUad36Cooperative,false)}
      {renderUad36DisplayField('Construction Method','uad_36_construction_method_type',isUad36URAROrPDR,isUad36URAROrPDR)}
    </div>;
  }

  if(state.declineReason==='Other')
    required = true;
  if(state.appraisal.status==='Assigned')
    actionBar =
    <div>
      <Card>
        <CardHeader className="gray-color">
          Action required - Accept assignment
        </CardHeader>
        <CardBody>
          <center>
            <b>You've been assigned a new order, the engagement letter can be found within the documents storage. Do you accept the assignment?</b><br/><font color="red">**By accepting the Assignment electronically, Appraiser confirms that the Engagement letter is in accordance with Appraiser’s understanding and agrees to the terms of this Assignment.**</font><br/><br/>
            <Button style={{width:'170px'}} color="warning" onClick={()=>setAcceptance('yes')}><i className="fa fa-check"></i> Accept</Button>&nbsp;&nbsp;&nbsp;&nbsp;
            <Button style={{width:'170px'}} color="warning" onClick={conditionPopUpToggle}><i className="fa fa-info"></i> Conditionally accept</Button>&nbsp;&nbsp;&nbsp;&nbsp;
            <Button style={{width:'170px'}} color="warning" onClick={declinePopUpToggle}><i className="fa fa-times"></i> Decline</Button>
          </center>

          <Modal className="my-modal" isOpen={state.declinePopUp} toggle={declinePopUpToggle} >
            <ModalHeader hidden={true} toggle={declinePopUpToggle}></ModalHeader>
            <ModalBody>
              <form onSubmit={(e)=>{e.preventDefault();setAcceptance('no')}}>
                <label>Reason</label>
                <Input required="true" type="select" onChange={(e)=>setState({declineReason:e.target.value})}>
                  <option key=""></option>
                  {
                    state.declineReasons.map(
                      (reason,index)=>{
                        return <option key={index} value={reason.reason}>{reason.reason}</option>
                      }
                    )
                  }
                </Input>
                <br/>
                <label>Comment</label>
                <Input required={required} onChange={(e)=>setState({declineComment:e.target.value})} type="textarea" rows="5" style={{resize:'none'}}></Input>
                <br/>

                <b><font color="red">*</font>By declining this assignment you will no longer be able to access this order.</b>
                <div className="my-divider">&nbsp;</div>
                <center>
                  <Button color="info" onClick={declinePopUpToggle}>Close</Button>&nbsp;
                  <Button color="danger">Decline</Button>
                </center>
              </form>
            </ModalBody>
          </Modal>



          <Modal className="my-modal" isOpen={state.conditionPopUp} toggle={conditionPopUpToggle} >
            <ModalHeader hidden={true} toggle={conditionPopUpToggle}></ModalHeader>
            <ModalBody>
              <form onSubmit={conditionallyAccept}>
                <label>Reason</label>
                <Input required="true" type="select" onChange={(e)=>setState({conditionReason:e.target.value})}>
                  <option key=""></option>
                  {
                    state.conditionReasons.map(
                      (reason,index)=>{
                        return <option key={index} value={reason.reason}>{reason.label}</option>
                      }
                    )
                  }
                </Input>
                <br/>
                {conditionFee}
                {conditionTurntime}
                <label>Comment</label>
                <Input required={required} onChange={(e)=>setState({conditionComment:e.target.value})} type="textarea" rows="5" style={{resize:'none'}}></Input>
                <br/>

                <b><font color="red">*</font>By conditionally accept this assignment, the order will be temporary taken away from you for consideration of your request.</b>
                <div className="my-divider">&nbsp;</div>
                <center>
                  <Button color="info" onClick={conditionPopUpToggle}>Close</Button>&nbsp;
                  <Button color="danger">Conditionally accept</Button>
                </center>
              </form>
            </ModalBody>
          </Modal>
      </CardBody>
      </Card>
      <br/>
    </div>
  else if(state.appraisal.status==='Pending Inspection')
    actionBar =
    <div>
      <Card>
        <CardHeader className="gray-color">
          Action required - Schedule inspection
        </CardHeader>
        <CardBody>
          <center>
            <b>Please schedule the inspection by picking a date & time by clicking the textbox below and then hit the "Schedule inspection" button.</b><br/><br/>
            <div className="display-inline" style={{width:'250px'}}>
              <DatePicker
                className="form-control"
                dateFormat='dd MMM yyyy hh:mm aa'
                showTimeSelect
                selected={inspectionDate}
                timeIntervals={15}
                onChange={(text)=>{setState({inspectionDate:moment(text).format("YYYY-MM-DD HH:mm")})}}
              />
            </div>
            <Button color="warning" disabled={state.submitDisabled!==false} onClick={buttonHandler}><i className="fa fa-clock"></i> Schedule inspection</Button>
          </center>
        </CardBody>
      </Card>
      <br/>
    </div>
  else if(state.appraisal.status==='Waiting On POC'){
    if(state.appraisal.datetime_inspection==='0000-00-00 00:00:00')
      actionBar =
      <div>
        <Card>
          <CardHeader className="gray-color">
            Action required - Schedule inspection
          </CardHeader>
          <CardBody>
            <center>
              <b>Please schedule the inspection by picking a date & time by clicking the textbox below and then hit the "Schedule inspection" button.</b><br/><br/>
              <div className="display-inline" style={{width:'250px'}}>
                <DatePicker
                  className="form-control"
                  dateFormat='dd MMM yyyy hh:mm aa'
                  showTimeSelect
                  selected={inspectionDate}
                  timeIntervals={15}
                  onChange={(text)=>{setState({inspectionDate:moment(text).format("YYYY-MM-DD HH:mm")})}}
                />
              </div>
              <Button color="warning" disabled={state.submitDisabled!==false} onClick={buttonHandler}><i className="fa fa-clock"></i> Schedule inspection</Button>
            </center>
          </CardBody>
        </Card>
        <br/>
      </div>
    else
    actionBar =
    <div>
      <Card>
        <CardHeader className="gray-color">
          Update inspection
        </CardHeader>
        <CardBody>
          <center>
            <b>The inspection already been scheduled, but if you want to update the inspection please pick a new date & time by clicking the textbox below then hit the "Update inspection" button.</b><br/><br/>
            <div className="display-inline" style={{width:'250px'}}>
              <DatePicker
                className="form-control"
                dateFormat='dd MMM yyyy hh:mm aa'
                showTimeSelect
                selected={inspectionDate}
                timeIntervals={15}
                onChange={(text)=>{setState({inspectionDate:moment(text).format("YYYY-MM-DD HH:mm")})}}
              />
            </div>
            <div className="display-inline" style={{width:'200px'}}><Input type="text" value={state.inspectionComment} onChange={(e)=>setState({inspectionComment:e.target.value})} placeholder="Reason to update"/></div>
            &nbsp;<Button color="warning" disabled={state.submitDisabled!==false} onClick={buttonHandler}><i className="fa fa-clock"></i> Update inspection</Button>
          </center>
        </CardBody>
      </Card>
      <br/>
    </div>
  } else if(state.appraisal.status==='Auto Assigning')
    actionBar =
    <div>
      <Card>
        <CardHeader className="gray-color">
          Action required - Schedule inspection
        </CardHeader>
        <CardBody>
          <center>
            <b>By scheduling an inspection, you are accepting this auto-assigned order.</b><br/><br/>
            <b>Please schedule the inspection by picking a date & time by clicking the textbox below and then hit the "Schedule inspection" button.</b><br/><br/>
            <div className="display-inline" style={{width:'250px'}}>
              <DatePicker
                className="form-control"
                dateFormat='dd MMM yyyy hh:mm aa'
                showTimeSelect
                selected={inspectionDate}
                timeIntervals={15}
                onChange={(text)=>{setState({inspectionDate:moment(text).format("YYYY-MM-DD HH:mm")})}}
              />
            </div>
            <Button color="warning" disabled={state.submitDisabled!==false} onClick={buttonHandler}><i className="fa fa-clock"></i> Accept Order & Schedule inspection</Button>
          </center>
        </CardBody>
      </Card>
      <br/>
    </div>
  else if(state.appraisal.status==='Inspection Scheduled'){

    let curInspectionDate = new Date(state.appraisal.datetime_inspection);
    let today = new Date();

    if(curInspectionDate&&curInspectionDate.getTime()<=today.getTime())
      actionBar =
      <div>
        <Card>
          <CardHeader className="gray-color">
            Action required - Inspection Completed
          </CardHeader>
          <CardBody>
            <center>
              <b>Did you completed the inspection? If yes please hit the "Inspection completed" button below to move the order forward.</b>
              <br/><br/>
              <Button color="warning" onClick={setInspectionCompleted}>Inspection completed</Button>
            </center>
          </CardBody>
        </Card>
        <br/>
      </div>

    else
    actionBar =
    <div>
      <Card>
        <CardHeader className="gray-color">
          Update inspection
        </CardHeader>
        <CardBody>
          <center>
            <b>The inspection already been scheduled, but if you want to update the inspection please pick a new date & time by clicking the textbox below then hit the "Update inspection" button.</b><br/><br/>
            <div className="display-inline" style={{width:'250px'}}>
              <DatePicker
                className="form-control"
                dateFormat='dd MMM yyyy hh:mm aa'
                showTimeSelect
                selected={inspectionDate}
                timeIntervals={15}
                onChange={(text)=>{setState({inspectionDate:moment(text).format("YYYY-MM-DD HH:mm")})}}
              />
            </div>
            <div className="display-inline" style={{width:'200px'}}><Input type="text" value={state.inspectionComment} onChange={(e)=>setState({inspectionComment:e.target.value})} placeholder="Reason to update"/></div>
            &nbsp;<Button color="warning" disabled={state.submitDisabled!==false} onClick={buttonHandler}><i className="fa fa-clock"></i> Update inspection</Button>
          </center>
        </CardBody>
      </Card>
      <br/>
    </div>
  }

  let specialInstructions;

  if(state.appraisal.special_instructions&&state.appraisal.special_instructions!==''){
    specialInstructions =
      <div className="floating-memo">
        <div onClick={floatingMemoToggle} className="cursor-pointer"><i className="fa fa-tag"></i>&nbsp;Special Instructions</div>
        <Collapse isOpen={state.dropdownOpen}>
          <Input type="textarea" value={state.appraisal.special_instructions} onChange={(e)=>{}} className="non-editable-textarea yellow-background-textarea margin-bottom" rows="20" name="text" id="exampleText">

          </Input>
        </Collapse>
      </div>
  }

  let fees, feesTotal = 0;

  if(state.appraisal.fees)
    fees = state.appraisal.fees.map(
      (fee,index)=>{
        feesTotal = feesTotal + parseInt(fee.amount,10);
        return (
          <Row key={index}>
            <Col sm="4">
              {fee.category}
            </Col>
            <Col sm="8">
              ${formatNumber(fee.amount)}
            </Col>
          </Row>
        );
      }
    )

  if(feesTotal !==0)
    feesTotal = <Row>
      <Col sm="4">
        <label>Total</label>
      </Col>
      <Col sm="8">
        ${formatNumber(feesTotal)}
      </Col>
    </Row>

  let rushPopUp;
  let uad36PopUp;

  if(state.appraisal && state.appraisal.rush==='Rush')
    rushPopUp = <Alert color="danger">
      This is a rush order.
    </Alert>;

  if(isUad36)
    uad36PopUp = <Alert color="danger">
      This is a UAD 3.6 order. Please upload UAD 3.6 zip.
    </Alert>;


  return (
    <div className="my-well">
        {specialInstructions}
      <Row>
        <Col sm="6">
          <div className="page-title">
            <i className="fa fa-reorder"></i>&nbsp;Appraisal Details
          </div>
          <NavLink to="/appraisal">Back to appraisal list</NavLink>
        </Col>
        <Col sm="6">

        </Col>
      </Row>
      <div className="my-divider">&nbsp;</div>

      <Nav tabs>
        <NavItem>
          <NavLink className="nav-link active" to="#">Appraisal Details</NavLink>
        </NavItem>
        <NavItem>
          <NavLink className="nav-link" to={"/condition/"+state.id}><span className="my-badge">{state.overallStatus.condition_count}</span> Condition</NavLink>
        </NavItem>
        <NavItem>
          <NavLink className="nav-link" to={"/rebuttal/"+state.id}><span className="my-badge">{state.overallStatus.rebuttal_count}</span> Rebuttal</NavLink>
        </NavItem>
        {pdrTab}
      </Nav>

      <br/>
      <a target="_blank" href={"https://msc.fema.gov/portal/search?AddressQuery="+encodeURIComponent(state.appraisal.property_street+(state.appraisal.property_unit_number ? ' ' + state.appraisal.property_unit_number : '')+' '+state.appraisal.property_city+' '+state.appraisal.property_state)}>Click here for FEMA Flood Map</a>

      {progressBar}

      <div className="card" style={{minHeight:'500px',borderTop:'0px'}}>
        <div className="card-header header-color">
          <i className="fa fa-home"></i>&nbsp;Ref # {state.appraisal.reference_num} - {state.appraisal.property_street}{state.appraisal.property_unit_number ? ' ' + state.appraisal.property_unit_number : ''} {state.appraisal.property_city}, {state.appraisal.property_state} {state.appraisal.property_zip} - {state.appraisal.borrower_f_name} {state.appraisal.borrower_l_name}
        </div>
        <div className="card-body">
          {pdr}
          {rushPopUp}
          {uad36PopUp}
          {actionBar}
          <div className="my-divider">&nbsp;</div>
          <Row>
            <Col sm="6">
              <Card>
                <CardHeader>
                  Assignment
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col sm="4">
                      <b>
                        <i className="fa fa-cogs"></i>&nbsp;
                        {isUad36 ? 'Product Summary' : 'Report Type(s)'}
                      </b>
                    </Col>
                    <Col sm="8">
                      {isUad36 ? (state.appraisal.uad_36_product_summary || 'None Selected') : appraisalType}
                    </Col>
                  </Row>
                  <Row><Col sm="12"><b><i className=" fa fa-dollar"></i>&nbsp;Fees</b></Col></Row>
                  {
                    fees
                  }
                  {
                    feesTotal
                  }
                </CardBody>
              </Card>
              <br/>
              {uad36ServiceScope}

              <Card>
                <CardHeader>
                  <b><i className="fa fa-info-circle"></i>&nbsp;{isUad36 ? 'Loan and Assignment' : 'Loan Facts'}</b>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col sm="4">
                      Loan Number
                    </Col>
                    <Col sm="8">
                      {state.appraisal.loan_num}
                    </Col>
                  </Row>

                  {isUad36 ? uad36LoanAssignment : <div>
                    <Row>
                      <Col sm="4">
                        Property Type
                      </Col>
                      <Col sm="8">
                        {state.appraisal.property_type}
                      </Col>
                    </Row>
                    <Row>
                      <Col sm="4">
                        Loan Type
                      </Col>
                      <Col sm="8">
                        {state.appraisal.loan_type}
                      </Col>
                    </Row>
                    {fhaCaseNumber}
                    <Row>
                      <Col sm="4">
                        Loan Purpose
                      </Col>
                      <Col sm="8">
                        {state.appraisal.loan_purpose}
                      </Col>
                    </Row>
                    {purchasePrice}
                  </div>}
                  {renderUad36DisplayField('Value Condition','uad_36_value_condition',true,false)}
                </CardBody>
              </Card>
              <br/>
              <Card>
                <CardHeader>
                  <b><i className=" fa fa-user-circle"></i>&nbsp;Lender</b>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col sm="4">
                      Company
                    </Col>
                    <Col sm="8">
                      {state.appraisal.client_name}
                    </Col>
                  </Row>
                  <Row className="red-color">
                    <Col sm="4">
                      Name On Report
                    </Col>
                    <Col sm="8">
                      {state.appraisal.name_on_report}
                    </Col>
                  </Row>
                  <Row>
                    <Col sm="4">
                      Address
                    </Col>
                    <Col sm="8">
                      {state.appraisal.client_address}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
              <br/>
             
            </Col>
            <Col sm="6">
              <Card>
                  <CardHeader>
                    <b><i className="fa fa-building"></i>&nbsp;Property Information</b>
                  </CardHeader>
                  <CardBody>
                    <Row>
                      <Col sm="4">
                        Street
                      </Col>
                      <Col sm="8">
                        {state.appraisal.property_street}
                      </Col>
                    </Row>
                    <Row>
                      <Col sm="4">
                        Unit #
                      </Col>
                      <Col sm="8">
                        {state.appraisal.property_unit_number}
                      </Col>
                    </Row>
                    <Row>
                      <Col sm="4">
                        County
                      </Col>
                      <Col sm="8">
                        {state.appraisal.property_county}
                      </Col>
                    </Row>
                    <Row>
                      <Col sm="4">
                        City
                      </Col>
                      <Col sm="8">
                        {state.appraisal.property_city}
                      </Col>
                    </Row>
                    <Row>
                      <Col sm="4">
                        State
                      </Col>
                      <Col sm="8">
                        {state.appraisal.property_state}
                      </Col>
                    </Row>
                    <Row>
                      <Col sm="4">
                        Zip
                      </Col>
                      <Col sm="8">
                        {state.appraisal.property_zip}
                      </Col>
                    </Row>
                    <Row>
                      <Col sm="4">
                        Occupancy
                      </Col>
                      <Col sm="8">
                        {state.appraisal.property_occupancy}
                      </Col>
                    </Row>
                    <Row>
                      <Col sm="4">
                        APN
                      </Col>
                      <Col sm="8">
                        {state.appraisal.apn}
                      </Col>
                    </Row>
                  <br/>
                  {uad36PropertyInformation}
                  </CardBody>
                </Card>
                
              <br/>
              <Card>
                <CardHeader>
                  <b><i className="fa fa-home"></i>&nbsp;Property Access</b>
                </CardHeader>
                <CardBody>
                  <Row className="red-color">
                    <Col sm="4">
                      Title
                    </Col>
                    <Col sm="8">
                      {state.appraisal.property_access_title}
                    </Col>
                  </Row>
                  <Row className="red-color">
                    <Col sm="4">
                      Name
                    </Col>
                    <Col sm="8">
                      {state.appraisal.property_access_name}
                    </Col>
                  </Row>
                  <Row className="red-color">
                    <Col sm="4">
                      Email
                    </Col>
                    <Col sm="8">
                      {state.appraisal.property_access_email}
                    </Col>
                  </Row>
                  <Row className="red-color">
                    <Col sm="4">
                      Work Phone #
                    </Col>
                    <Col sm="8">
                      {state.appraisal.property_access_work_phone}
                    </Col>
                  </Row>
                  <Row className="red-color">
                    <Col sm="4">
                      Home Phone #
                    </Col>
                    <Col sm="8">
                      {state.appraisal.property_access_home_phone}
                    </Col>
                  </Row>
                  <Row className="red-color">
                    <Col sm="4">
                      Mobile Phone #
                    </Col>
                    <Col sm="8">
                      {state.appraisal.property_access_mobile_phone}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
              <br/>
              <Card>
                <CardHeader>
                  <b><i className="fa fa-user-circle"></i>&nbsp;Borrower</b>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col sm="4">
                      First Name
                    </Col>
                    <Col sm="8">
                      {state.appraisal.borrower_f_name}
                    </Col>
                  </Row>
                  <Row>
                    <Col sm="4">
                      Last Name
                    </Col>
                    <Col sm="8">
                      {state.appraisal.borrower_l_name}
                    </Col>
                  </Row>
                  <Row>
                    <Col sm="4">
                      Phone
                    </Col>
                    <Col sm="8">
                      {state.appraisal.borrower_phone}
                    </Col>
                  </Row>
                  <Row>
                    <Col sm="4">
                      Email
                    </Col>
                    <Col sm="8">
                      {state.appraisal.borrower_email}
                    </Col>
                  </Row>
                  {
                    state.extraBorrowers.map(
                      (borrower,index)=>{
                        return (
                          <div key={index}>
                            <br/>
                            <Row>
                              <Col sm="12">
                                <b>{borrower.entity_label}</b>
                              </Col>
                              <Col sm="4">
                                Name
                              </Col>
                              <Col sm="8">
                                {borrower.entity_name}
                              </Col>
                              <Col sm="4">
                                Email
                              </Col>
                              <Col sm="8">
                                {borrower.entity_email}
                              </Col>
                              <Col sm="4">
                                Phone
                              </Col>
                              <Col sm="8">
                                {borrower.entity_phone}
                              </Col>
                            </Row>
                          </div>
                        )
                      }
                    )
                  }
                </CardBody>
              </Card>
              <br/>
              
              
              <br/>
              <Card>
                <CardHeader>
                  <b><i className="fa fa-calendar"></i>&nbsp;Dates</b>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col sm="4">
                      Inspection:
                    </Col>
                    <Col sm="8">
                      {formatDateTime(state.appraisal.datetime_inspection)}
                    </Col>
                  </Row>
                  <Row>
                    <Col sm="4">
                      Report Due:
                    </Col>
                    <Col sm="8">
                      {formatDateTime(state.appraisal.datetime_due_local)} Local Time
                    </Col>
                  </Row>
                </CardBody>
              </Card>
              <br/>
            </Col>
          </Row>
        </div>
      </div>
      <br/>
      <Row>
        <Col sm="6">
          <div className="card" style={{minHeight:'300px'}}>
            <div className="card-header header-color">
              <i className="fa fa-files"></i>&nbsp;Documents Storage
            </div>
            <div className="card-body">
              {appraisalFileUI}
            </div>
          </div>
        </Col>
        <Col sm="6">
          <div className="card" style={{minHeight:'300px'}}>
            <div className="card-header header-color">
              <i className="fa fa-comments"></i>&nbsp;Communications Log
            </div>
            <div className="card-body">
              {appraisalCommentUI}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
}


export default Appraisal;
